import React from 'react';
import { motion } from 'framer-motion';

interface KombyLogoProps {
  className?: string;
  animate?: boolean;
  /** Display size hint for width/height attrs (header default 40). */
  size?: number;
}

/** Compact WebP (~2 KiB) for header LCP — full asset remains /logokomby.webp. */
const LOGO_SRC = '/logokomby-40.webp';
const LOGO_SRC_2X = '/logokomby-header.webp';

export const KombyLogo: React.FC<KombyLogoProps> = ({
  className = 'w-16 h-16',
  animate = true,
  size = 40,
}) => {
  const logoVariants = {
    initial: {
      scale: 0,
      opacity: 0,
      rotate: -180,
    },
    animate: {
      scale: 1,
      opacity: 1,
      rotate: 0,
      transition: {
        duration: 1,
        ease: 'easeOut',
        type: 'spring',
        stiffness: 100,
      },
    },
    hover: {
      rotate: 10,
      scale: 1.1,
      transition: {
        duration: 0.3,
        ease: 'easeInOut',
      },
    },
  };

  const sharp = size >= 200;
  const imgProps = {
    src: sharp ? '/logokomby.webp' : LOGO_SRC,
    srcSet: sharp
      ? '/logokomby-header.webp 283w, /logokomby.webp 765w'
      : `${LOGO_SRC} ${size}w, ${LOGO_SRC_2X} 283w`,
    sizes: `${size}px`,
    alt: 'Komby Logo',
    width: sharp ? 765 : size,
    height: sharp ? 216 : size,
    decoding: 'async' as const,
    // Never lazy-load header logo (PSI). Avoid fetchPriority camelCase warning on React 18.
    className: `${className} object-contain`,
  };

  if (animate) {
    return (
      <motion.img
        {...imgProps}
        variants={logoVariants}
        initial="initial"
        animate="animate"
        whileHover="hover"
      />
    );
  }

  return <img {...imgProps} />;
};
